// "Position on page" helpers shared by LeftSection and panels (single + multi selection).
// Moved verbatim from LeftSection.vue. Do NOT rename.
// The host component must provide: width, height, productCrop, selectedObjectsList, activeObjectForLeftPanel,
// isMultiPropertySelection (panelContext).
// shownAlignment + onPositionAlign drive <position-controls> in the panels.
import EventBus from '@/EventBus';
import ApplicationEventTypes from '@/mixins/ApplicationEventTypes';

const HORIZONTAL = ['left', 'center', 'right'];

export default {
  data() {
    return {
      // Last "Position on page" button clicked. The canvas alignment computeds read the DOM and do not always
      // recompute after an align, so this keeps the clicked button highlighted (the old radio inputs did the
      // same natively). It only applies while the objects stay where that click put them (clickedAtPosition).
      clickedAlignment:  { horizontal: null, vertical: null },
      clickedAtPosition: null,
    };
  },
  computed: {
    alignmentTargetsPosition() {
      const targets = this.isMultiPropertySelection ? this.getSelectedObjectsList : [this.activeObjectForLeftPanel];
      return targets.filter(Boolean).map(obj => `${obj.Id}:${obj.X}:${obj.Y}`).join('|');
    },
    activeClickedAlignment() {
      return this.clickedAtPosition === this.alignmentTargetsPosition
          ? this.clickedAlignment
          : { horizontal: null, vertical: null };
    },
    shownAlignment() {
      const fromCanvas = this.isMultiPropertySelection ? this.getObjectsCanvasAlignment : this.getObjectCanvasAlignment;
      return {
        horizontal: this.activeClickedAlignment.horizontal || fromCanvas.horizontalAlignment,
        vertical:   this.activeClickedAlignment.vertical || fromCanvas.verticalAlignment,
      };
    },
    getObjectCanvasAlignment() {
      let position = { left: 'left', top: 'top' };
      if (this.activeObjectForLeftPanel) {
        let objectElement = document.getElementById(this.activeObjectForLeftPanel.Id);
        if (objectElement) {
          objectElement = objectElement.getBoundingClientRect();
        } else {
          position = {
            horizontalAlignment: 'left',
            verticalAlignment:   'top',
          };
          return position;
        }
        const mainHtmlElement       = document.getElementById('main_html').getBoundingClientRect();
        const horizontalCenterCheck = parseInt((this.width - this.activeObjectForLeftPanel.Width) / 2);
        const verticalCenterCheck   = parseInt((this.height - this.activeObjectForLeftPanel.Height) / 2);
        let horizontalAlignment     = '';
        let verticalAlignment       = '';
        if (parseInt(objectElement.left) > parseInt(mainHtmlElement.left) - 2 && parseInt(objectElement.left) < parseInt(mainHtmlElement.left) + 2) {
          horizontalAlignment = 'left';
        }
        if (parseInt(objectElement.right) > parseInt(mainHtmlElement.right) - 2 && parseInt(objectElement.right) < parseInt(mainHtmlElement.right) + 2) {
          horizontalAlignment = 'right';
        }
        if (this.activeObjectForLeftPanel.X > horizontalCenterCheck - 2 && this.activeObjectForLeftPanel.X < horizontalCenterCheck + 2) {
          horizontalAlignment = 'center';
        }
        if (parseInt(objectElement.top) > parseInt(mainHtmlElement.top) - 2 && parseInt(objectElement.top) < parseInt(mainHtmlElement.top) + 2) {
          verticalAlignment = 'top';
        }
        if (this.activeObjectForLeftPanel.Y > verticalCenterCheck - 2 && this.activeObjectForLeftPanel.Y < verticalCenterCheck + 2) {
          verticalAlignment = 'center';
        }
        if (parseInt(objectElement.bottom) > parseInt(mainHtmlElement.bottom) - 2 && parseInt(objectElement.bottom) < parseInt(mainHtmlElement.bottom) + 2) {
          verticalAlignment = 'bottom';
        }
        position = {
          horizontalAlignment,
          verticalAlignment,
        };
      }
      return position;
    },
    getObjectsCanvasAlignment() { // For multiple objects
      const { selectedObjectsList } = this;

      // Get the canvas or main container element
      const mainHtml = document.getElementById('main_html');

      // If canvas or selected objects are not available, return default alignment
      if (!mainHtml || !selectedObjectsList.length) {
        return { horizontalAlignment: 'left', verticalAlignment: 'top' };
      }

      const mainRect = mainHtml.getBoundingClientRect();

      // Variables to store alignment of the first object for comparison
      let firstHorizontal = null;
      let firstVertical   = null;

      // Flags to track if all objects share the same alignment
      let isConsistentHorizontal = true;
      let isConsistentVertical   = true;

      for (let i = 0; i < selectedObjectsList.length; i++) {
        const obj = selectedObjectsList[i];

        // Get the actual DOM element for the object
        const el = document.querySelector(`.canvas-main-html-container [id='${obj.Id}']`);
        console.log('el', el);
        if (!el) {
          continue;
        }

        const rect = el.getBoundingClientRect();

        // Calculate expected center positions based on canvas size and object dimensions
        const horizontalCenter = Math.round((this.width - obj.Width) / 2);
        const verticalCenter   = Math.round((this.height - obj.Height) / 2);

        // Determine horizontal and vertical alignment for this object
        let hAlign = '';
        let vAlign = '';

        // Horizontal alignment checks: left, right, center
        if (Math.abs(rect.left - mainRect.left) <= 2) {
          hAlign = 'left';
        } else if (Math.abs(rect.right - mainRect.right) <= 2) {
          hAlign = 'right';
        } else if (Math.abs(obj.X - horizontalCenter) <= 2) {
          hAlign = 'center';
        }

        // Vertical alignment checks: top, bottom, center
        if (Math.abs(rect.top - mainRect.top) <= 2) {
          vAlign = 'top';
        } else if (Math.abs(rect.bottom - mainRect.bottom) <= 2) {
          vAlign = 'bottom';
        } else if (Math.abs(obj.Y - verticalCenter) <= 2) {
          vAlign = 'center';
        }

        // For the first object, store its alignment values
        if (i === 0) {
          firstHorizontal = hAlign;
          firstVertical   = vAlign;
        } else {
          // For subsequent objects, check if alignment differs
          if (firstHorizontal !== hAlign) {
            isConsistentHorizontal = false;
          }
          if (firstVertical !== vAlign) {
            isConsistentVertical = false;
          }
        }
      }

      // Return alignment only if consistent across all objects, otherwise return null
      const position = {
        horizontalAlignment: isConsistentHorizontal ? firstHorizontal : null,
        verticalAlignment:   isConsistentVertical ? firstVertical : null,
      };
      console.log('position', position);
      return position;
    },
  },
  methods: {
    onPositionAlign(value) {
      const axis  = HORIZONTAL.indexOf(value) !== -1 ? 'horizontal' : 'vertical';
      const state = value === 'middle' ? 'center' : value;
      if (this.shownAlignment[axis] === state) {
        return; // like the old radio inputs: clicking the checked option does nothing
      }
      this.clickedAlignment = { ...this.activeClickedAlignment, [axis]: state };
      this.alignObjectPosition(value, this.productCrop);
      this.clickedAtPosition = this.alignmentTargetsPosition;
    },
    alignObjectPosition(value, productCrop) {
      EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.UPDATE_OBJECT_ALIGNMENT, { value, productCrop });
    },
  },
};
